import * as React from 'react'
import { ChevronsUpDown, Check } from 'lucide-react'
import { useFormContext } from 'react-hook-form'
import { Button } from '@/components/ui/button'
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '@/components/ui/command'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form'
import { cn } from '@/lib/utils'
import { useAccountStores } from '@/features/accounts/api/get-account-stores'

type StoresComboboxProps = {
    accountId: string
    name: string
    label?: string
    placeholder?: string
}

export default function StoresCombobox({ accountId, name, label = 'Store', placeholder = 'Select store' }: StoresComboboxProps) {
    const form = useFormContext()
    const enabled = Boolean(accountId)
    const { data, isLoading } = useAccountStores({ accountId, queryConfig: { enabled } as any })
    const stores = React.useMemo(() => {
        const arr = (data as unknown as any[]) ?? []
        return arr.map((s) => ({
            label: s.friendlyFFLName ?? s.storeName ?? s.id ?? s.storeId,
            value: s.id ?? s.storeId ?? s.name,
        }))
    }, [data])
    const [open, setOpen] = React.useState(false)

    return (
        <FormField
            control={form.control}
            name={name}
            render={({ field }) => (
                <FormItem className='flex flex-col w-full'>
                    {label && <FormLabel>{label}</FormLabel>}
                    <Popover open={open} onOpenChange={setOpen}>
                        <PopoverTrigger asChild>
                            <FormControl>
                                <Button
                                    variant='outline'
                                    role='combobox'
                                    disabled={!enabled}
                                    className={cn('justify-between', (!field.value || !enabled) && 'text-muted-foreground')}
                                >
                                    {!enabled
                                        ? 'Select account first'
                                        : field.value
                                            ? stores.find((a) => a.value === field.value)?.label
                                            : (isLoading ? 'Loading…' : placeholder)}
                                    <ChevronsUpDown className='opacity-50' />
                                </Button>
                            </FormControl>
                        </PopoverTrigger>
                        <PopoverContent className='p-0'>
                            <Command>
                                <CommandInput placeholder='Search stores…' className='h-9' />
                                <CommandList>
                                    <CommandEmpty>No stores found.</CommandEmpty>
                                    <CommandGroup>
                                        {stores.map((store) => (
                                            <CommandItem
                                                key={store.value}
                                                value={store.label}
                                                onSelect={() => {
                                                    form.setValue(name as any, store.value, { shouldDirty: true })
                                                    setOpen(false)
                                                }}
                                            >
                                                {store.label}
                                                <Check className={cn('ml-auto', store.value === field.value ? 'opacity-100' : 'opacity-0')} />
                                            </CommandItem>
                                        ))}
                                    </CommandGroup>
                                </CommandList>
                            </Command>
                        </PopoverContent>
                    </Popover>
                    <FormMessage />
                </FormItem>
            )}
        />
    )
}


